import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { 
    CircularProgress,
    Box, 
    Typography, 
    Grid, 
    TextField, 
    Button, 
    Paper, 
    IconButton, 
    Card, 
    Autocomplete, 
    Chip, 
    Divider,
    Switch,
    FormControlLabel,
    List,
    Tooltip,
    ThemeProvider,
    createTheme,
    CssBaseline
} from '@mui/material';
import { 
    MapContainer, 
    TileLayer, 
    Marker, 
    Polyline, 
    useMap, 
    Tooltip as LeafletTooltip 
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import RouteIcon from '@mui/icons-material/Route';
import SaveIcon from '@mui/icons-material/Save';
import TimerIcon from '@mui/icons-material/Timer';
import StraightenIcon from '@mui/icons-material/Straighten';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import Cookies from 'js-cookie';
import api from '../../../../../services/api';
import NavegadorGestao from '../../../../../components/NavBarGestao';

// Fix para ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Ícones customizados para o mapa
const iconStart = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/3011/3011985.png', // Casa/Base
    iconSize: [35, 35],
    iconAnchor: [17, 35]
});

const iconRound = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png', // Marcador azul
    iconSize: [30, 30],
    iconAnchor: [15, 30]
});

const iconEnd = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/2618/2618245.png', // Bandeira/Chegada
    iconSize: [35, 35],
    iconAnchor: [17, 35]
});

// Função para calcular distância entre dois pontos (Haversine em KM)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Raio da Terra em KM
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

// Componente para ajustar o zoom do mapa
function RecenterMap({ routePoints, startPoint, endPoint }) {
    const map = useMap();
    useEffect(() => {
        const allPoints = [...routePoints];
        if (startPoint) allPoints.push(startPoint);
        if (endPoint) allPoints.push(endPoint);

        if (allPoints.length > 0) {
            const validPoints = allPoints.filter(p => !isNaN(parseFloat(p.latitude)) && !isNaN(parseFloat(p.longitude)));
            if (validPoints.length > 0) {
                const bounds = L.latLngBounds(validPoints.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]));
                map.fitBounds(bounds, { padding: [50, 50] });
            }
        }
    }, [routePoints, startPoint, endPoint, map]);
    return null;
}

const DIAS_SEMANA = [
    { key: 'SEG', label: 'Seg' }, { key: 'TER', label: 'Ter' }, { key: 'QUA', label: 'Qua' },
    { key: 'QUI', label: 'Qui' }, { key: 'SEX', label: 'Sex' }, { key: 'SAB', label: 'Sáb' },
    { key: 'DOM', label: 'Dom' }
];

export default function CadastrarRotaMotorizada() {
    const { id } = useParams();
    const [darkMode, setDarkMode] = useState(Cookies.get('darkMode') === 'true');
    const [routeName, setRouteName] = useState('');
    const [instalacoes, setInstalacoes] = useState([]);
    const [loadingInstalacoes, setLoadingInstalacoes] = useState(false);
    const [routePoints, setRoutePoints] = useState([]);
    const [startPoint, setStartPoint] = useState(null);
    const [endPoint, setEndPoint] = useState(null);
    const [saving, setSaving] = useState(false);
    const [filterCity, setFilterCity] = useState('');
    const [filterState, setFilterState] = useState('');
    const [autocompleteKey, setAutocompleteKey] = useState(0);

    // Novos estados para Rondantes
    const [executors, setExecutors] = useState([]);
    const [usuariosRondantes, setUsuariosRondantes] = useState([]);
    const [loadingUsuarios, setLoadingUsuarios] = useState(false);
    const [autocompleteExecutorKey, setAutocompleteExecutorKey] = useState(0);

    useEffect(() => {
        if (id) {
            const fetchRota = async () => {
                try {
                    const res = await api.get(`v1/protected/rotarondamotorizada/listarporid/${id}`, {
                        headers: { Authorization: `Bearer ${Cookies.get('token')}` }
                    });
                    const rota = res.data;
                    setRouteName(rota.nome_rota);
                    setStartPoint(rota.ponto_partida);
                    setEndPoint(rota.ponto_final);
                    setRoutePoints(rota.pontos.map(p => ({
                        ...p.instalacao,
                        tempId: p.id_ponto_rota_ronda_motorizada,
                        isRonda: !!p.is_ronda, // Garante que seja booleano
                        dias: p.dias_semana ? p.dias_semana.split(',') : []
                    })));
                    
                    if (rota.executores) {
                        setExecutors(rota.executores.map(e => ({
                            ...e.usuario,
                            tempId: e.id_rota_executor,
                            dias: e.dias_semana ? e.dias_semana.split(',') : [],
                            horaInicio: e.hora_inicio || '',
                            horaFim: e.hora_fim || ''
                        })));
                    }
                } catch (err) {
                    console.error("Erro ao buscar rota", err);
                }
            };
            fetchRota();
        }
    }, [id]);

    // Tema dinâmico
    const theme = useMemo(() => createTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
            background: { default: darkMode ? '#121212' : '#f5f5f5', paper: darkMode ? '#1e1e1e' : '#ffffff' }
        }
    }), [darkMode]);

    useEffect(() => {
        const handleDarkModeChange = () => setDarkMode(Cookies.get('darkMode') === 'true');
        window.addEventListener('darkModeChanged', handleDarkModeChange);
        return () => window.removeEventListener('darkModeChanged', handleDarkModeChange);
    }, []);

    useEffect(() => {
        const fetchInstalacoes = async () => {
            setLoadingInstalacoes(true);
            try {
                const res = await api.get('/v1/protected/clientes/instalacoes/listartodos', {
                    headers: { Authorization: `Bearer ${Cookies.get('token')}` }
                });
                setInstalacoes(res.data || []);
            } catch (err) {
                console.error("Erro ao buscar instalações", err);
            } finally {
                setLoadingInstalacoes(false);
            }
        };
        fetchInstalacoes();

        const fetchUsuarios = async () => {
            setLoadingUsuarios(true);
            try {
                const res = await api.get('/v1/protected/user/listartodos', {
                    headers: { Authorization: `Bearer ${Cookies.get('token')}` }
                });
                // Filtra usuários ativos e que têm a permissão/status de ronda_motorizada
                const rondantes = (res.data || []).filter(u => u.status === 1 && u.ronda_motorizada === 1);
                setUsuariosRondantes(rondantes);
            } catch (err) {
                console.error("Erro ao buscar usuários", err);
            } finally {
                setLoadingUsuarios(false);
            }
        };
        fetchUsuarios();
    }, []);

    // Estatísticas da rota
    const routeStats = useMemo(() => {
        let totalKm = 0;
        let totalExecutionTime = 0; // em minutos

        const allPoints = [];
        if (startPoint && !isNaN(parseFloat(startPoint.latitude))) allPoints.push(startPoint);
        allPoints.push(...routePoints);
        if (endPoint && !isNaN(parseFloat(endPoint.latitude))) allPoints.push(endPoint);

        allPoints.forEach((p, index) => {
            if (p.isRonda) totalExecutionTime += 15;
            if (index > 0) {
                const prev = allPoints[index - 1];
                totalKm += calculateDistance(
                    parseFloat(prev.latitude), parseFloat(prev.longitude),
                    parseFloat(p.latitude), parseFloat(p.longitude)
                );
            }
        });

        // Estimativa de tempo de deslocamento (40km/h médio)
        const travelTime = (totalKm / 40) * 60; 
        const totalTime = travelTime + totalExecutionTime;

        return {
            distance: totalKm.toFixed(2),
            time: Math.round(totalTime)
        };
    }, [routePoints, startPoint, endPoint]);

    const handleAddPoint = (instalacao) => {
        if (!instalacao || isNaN(parseFloat(instalacao.latitude))) {
            return;
        }
        setRoutePoints(prev => [...prev, {
            ...instalacao,
            tempId: Date.now() + Math.random(),
            isRonda: true,
            dias: ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM']
        }]);
        setAutocompleteKey(prev => prev + 1);
    };

    const handleAddExecutor = (user) => {
        if (!user) return;
        // Avoid adding same user twice
        if (executors.find(e => e.id_usuario === user.id_usuario)) return;

        setExecutors(prev => [...prev, {
            ...user,
            tempId: Date.now() + Math.random(),
            dias: ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'],
            horaInicio: '08:00',
            horaFim: '18:00'
        }]);
        setAutocompleteExecutorKey(prev => prev + 1);
    };

    const handleSave = async () => {
        if (!routeName) return alert("Informe o nome da rota");
        if (!startPoint) return alert("Selecione o ponto de partida");
        if (!endPoint) return alert("Selecione o ponto de chegada");
        if (routePoints.length === 0) return alert("Adicione ao menos um ponto de ronda");

        setSaving(true);
        const payload = {
            id_rota_ronda_motorizada: id ? parseInt(id) : undefined,
            nome_rota: routeName,
            ponto_partida: { id_instalacao: startPoint.id_instalacao },
            ponto_final: { id_instalacao: endPoint.id_instalacao },
            pontos: routePoints.map((p, index) => {
                // Se o tempId for o id do banco (menor que 1 bilhão para segurança, 
                // já que Date.now() é ~1.7 trilhão), enviamos como ID real.
                const isRealId = p.tempId && p.tempId < 1000000000; 
                
                return {
                    id_ponto_rota_ronda_motorizada: isRealId ? p.tempId : undefined,
                    instalacao: { id_instalacao: p.id_instalacao },
                    ordem: index,
                    dias_semana: (p.dias || []).join(','),
                    is_ronda: p.isRonda
                };
            }),
            executores: executors.map(e => {
                const isRealId = e.tempId && e.tempId < 1000000000; 
                return {
                    id_rota_executor: isRealId ? e.tempId : undefined,
                    usuario: { id_usuario: e.id_usuario },
                    dias_semana: (e.dias || []).join(','),
                    hora_inicio: e.horaInicio,
                    hora_fim: e.horaFim
                };
            })
        };

        try {
            const headers = { 'Authorization': 'Bearer ' + Cookies.get("token") };
            const response = await api.post("v1/protected/rotarondamotorizada/cadastrar", payload, { headers });
            const newId = response.data.id_rota_ronda_motorizada;
            alert("Rota salva com sucesso!");
            
            // Redireciona para a página de edição da rota recém-criada (ou atualizada)
            window.location.href = `/gestao/operacional/rondamotorizada/rotas/editar/${newId}`;
        } catch (error) {
            console.error(error);
            alert("Erro ao cadastrar rota");
        } finally {
            setSaving(false);
        }
    };

    const handleRemovePoint = (index) => setRoutePoints(prev => prev.filter((_, i) => i !== index));

    const movePoint = (index, direction) => {
        setRoutePoints(prev => {
            const newList = [...prev];
            const target = direction === 'up' ? index - 1 : index + 1;
            if (target < 0 || target >= newList.length) return prev;
            [newList[index], newList[target]] = [newList[target], newList[index]];
            return newList;
        });
    };

    const toggleDay = (pointIndex, dayKey) => {
        setRoutePoints(prev => prev.map((p, i) => i === pointIndex 
            ? { ...p, dias: p.dias.includes(dayKey) ? p.dias.filter(d => d !== dayKey) : [...p.dias, dayKey] } 
            : p
        ));
    };

    const toggleIsRonda = (index) => {
        setRoutePoints(prev => prev.map((p, i) => i === index ? { ...p, isRonda: !p.isRonda } : p));
    };

    const handleRemoveExecutor = (index) => setExecutors(prev => prev.filter((_, i) => i !== index));

    const toggleExecutorDay = (index, dayKey) => {
        setExecutors(prev => prev.map((e, i) => i === index 
            ? { ...e, dias: e.dias.includes(dayKey) ? e.dias.filter(d => d !== dayKey) : [...e.dias, dayKey] } 
            : e
        ));
    };

    const handleExecutorTimeChange = (index, field, value) => {
        setExecutors(prev => prev.map((e, i) => i === index ? { ...e, [field]: value } : e));
    };

    const polylinePositions = useMemo(() => {
        const positions = routePoints.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]).filter(pos => !isNaN(pos[0]));
        if (startPoint && !isNaN(parseFloat(startPoint.latitude))) {
            positions.unshift([parseFloat(startPoint.latitude), parseFloat(startPoint.longitude)]);
        }
        if (endPoint && !isNaN(parseFloat(endPoint.latitude))) {
            positions.push([parseFloat(endPoint.latitude), parseFloat(endPoint.longitude)]);
        }
        return positions;
    }, [routePoints, startPoint, endPoint]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
                <NavegadorGestao Operacional={true} RondaMotorizada={true} corFundo={darkMode ? '#1e1e1e' : 'white'} travado={true} corTexto={darkMode ? 'white' : 'black'} />
                
                <Grid container sx={{ flex: 1, overflow: 'hidden', p: 0.5, gap: 1 }}>
                    {/* CONFIGURAÇÃO */}
                    <Grid item xs={12} md={3.5} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Paper elevation={3} sx={{ p: 1, borderRadius: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <RouteIcon color="primary" sx={{ fontSize: 18 }} /> {id ? 'Editar Rota' : 'Configurar Rota'}
                                    </Typography>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => window.location.href = '/gestao/operacional/rondamotorizada/index'}
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        <ArrowBackIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <TextField fullWidth size="small" label="Nome da Rota" value={routeName} onChange={(e) => setRouteName(e.target.value)} />
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField 
                                            fullWidth 
                                            label="Filtrar por Cidade" 
                                            variant="outlined" 
                                            size="small" 
                                            value={filterCity} 
                                            onChange={(e) => setFilterCity(e.target.value)} 
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField 
                                            fullWidth 
                                            label="Filtrar por Estado" 
                                            variant="outlined" 
                                            size="small" 
                                            value={filterState} 
                                            onChange={(e) => setFilterState(e.target.value)} 
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Autocomplete
                                            options={instalacoes.filter(inst => {
                                                // Pode ser igual ao endPoint, mas não pode estar nas rondas
                                                const isSelected = routePoints.some(p => p.id_instalacao === inst.id_instalacao);
                                                return !isSelected;
                                            })}
                                            value={startPoint}
                                            onChange={(e, v) => setStartPoint(v)}
                                            getOptionLabel={(option) => `${option.nome_fantasia || option.nome_empresarial}`}
                                            renderInput={(params) => <TextField {...params} label="Partida" variant="standard" size="small" />}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Autocomplete
                                            size="small"
                                            options={instalacoes.filter(inst => {
                                                // Pode ser igual ao startPoint, mas não pode estar nas rondas
                                                const isSelected = routePoints.some(p => p.id_instalacao === inst.id_instalacao);
                                                return !isSelected;
                                            })}
                                            value={endPoint}
                                            onChange={(e, v) => setEndPoint(v)}
                                            getOptionLabel={(option) => `${option.nome_fantasia || option.nome_empresarial}`}
                                            renderInput={(params) => <TextField {...params} label="Chegada" variant="standard" size="small" />}
                                        />
                                    </Grid>
                                </Grid>

                                <Autocomplete
                                    key={autocompleteKey}
                                    size="small"
                                    value={null}
                                    options={instalacoes.filter(inst => {
                                        // 1. Filtrar por Cidade/Estado
                                        const searchStr = `${inst.nome_fantasia} ${inst.nome_empresarial} ${inst.razao_social} ${inst.logradouro} ${inst.cidade} ${inst.bairro}`.toLowerCase();
                                        const matchesCity = filterCity === '' || searchStr.includes(filterCity.toLowerCase()) || (inst.cidade && inst.cidade.toLowerCase().includes(filterCity.toLowerCase()));
                                        const matchesState = filterState === '' || (inst.estado && inst.estado.toLowerCase().includes(filterState.toLowerCase()));
                                        
                                        if (!matchesCity || !matchesState) return false;

                                        // 2. Excluir já selecionados
                                        const isSelected = (startPoint?.id_instalacao === inst.id_instalacao) || 
                                                           (endPoint?.id_instalacao === inst.id_instalacao) || 
                                                           routePoints.some(p => p.id_instalacao === inst.id_instalacao);
                                        
                                        return !isSelected;
                                    })}
                                    loading={loadingInstalacoes}
                                    getOptionLabel={(option) => `${option.identificador_integrador} - ${option.nome_fantasia || option.nome_empresarial}`}
                                    getOptionDisabled={(option) => isNaN(parseFloat(option.latitude))}
                                    onChange={(e, v) => handleAddPoint(v)}
                                    renderOption={(props, option) => {
                                        const hasCoords = !isNaN(parseFloat(option.latitude));
                                        return (
                                            <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', opacity: hasCoords ? 1 : 0.6 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {option.identificador_integrador} - {option.nome_fantasia || option.nome_empresarial}
                                                    </Typography>
                                                    <Typography variant="caption" color={hasCoords ? "text.secondary" : "error"} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {option.cidade ? `${option.cidade} / ${option.estado || ''}` : 'Endereço não informado'}
                                                        {!hasCoords && <b> • (SEM COORDENADAS)</b>}
                                                    </Typography>
                                                </Box>
                                                <Tooltip title="Editar Instalação">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            window.open(`/gestao/administracao/clientes/gerenciarcliente/instalacao/gerenciarinstalacao/${option.id_instalacao}/${option.id_cliente}`, '_blank');
                                                        }}
                                                    >
                                                        <SettingsIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        );
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Adicionar Pontos de Ronda" variant="filled" size="small" />}
                                />
                            </Box>

                            <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
                                <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {routePoints.map((point, index) => (
                                        <Card key={point.tempId} variant="outlined" sx={{ 
                                            p: 0.75, borderRadius: 2, borderLeft: '4px solid', 
                                            borderLeftColor: point.isRonda ? 'primary.main' : 'warning.main',
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.02)' : '#fff'
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="caption" fontWeight="bold" color="primary" sx={{ fontSize: '0.65rem' }}>PONTO {index + 1}</Typography>
                                                    <Typography variant="body2" fontWeight="bold" noWrap sx={{ fontSize: '0.8rem' }}>
                                                        {point.nome_fantasia || point.nome_empresarial}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <IconButton size="small" onClick={() => movePoint(index, 'up')} disabled={index === 0} sx={{ p: 0.5 }}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton>
                                                    <IconButton size="small" onClick={() => movePoint(index, 'down')} disabled={index === routePoints.length - 1} sx={{ p: 0.5 }}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></IconButton>
                                                    <IconButton size="small" color="error" onClick={() => handleRemovePoint(index)} sx={{ p: 0.5 }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                                                </Box>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                                <FormControlLabel 
                                                    control={<Switch size="small" checked={point.isRonda} onChange={() => toggleIsRonda(index)} />} 
                                                    label={<Typography sx={{ fontSize: '0.7rem' }}>Fazer Ronda</Typography>} 
                                                    sx={{ m: 0 }}
                                                />
                                                {point.isRonda && (
                                                    <Box sx={{ display: 'flex', gap: 0.2 }}>
                                                        {['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'].map(dia => (
                                                            <Box 
                                                                key={dia}
                                                                onClick={() => toggleDay(index, dia)}
                                                                sx={{ 
                                                                    fontSize: '0.6rem', padding: '2px 4px', borderRadius: '4px', cursor: 'pointer',
                                                                    bgcolor: point.dias.includes(dia) ? 'primary.main' : 'rgba(128,128,128,0.1)',
                                                                    color: point.dias.includes(dia) ? 'white' : 'text.secondary',
                                                                    transition: '0.2s'
                                                                }}
                                                            >
                                                                {dia[0]}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Card>
                                    ))}
                                </List>

                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    RONDANTES DESIGNADOS
                                </Typography>
                                <Box sx={{ mb: 1 }}>
                                    <Autocomplete
                                        key={autocompleteExecutorKey}
                                        size="small"
                                        value={null}
                                        options={usuariosRondantes}
                                        getOptionLabel={(option) => option.nome_amigavel || option.login}
                                        onChange={(e, v) => handleAddExecutor(v)}
                                        renderInput={(params) => <TextField {...params} label="Adicionar Rondante" variant="filled" size="small" />}
                                    />
                                </Box>
                                {executors.map((executor, index) => (
                                    <Card key={executor.tempId} variant="outlined" sx={{ p: 0.75, mb: 1, borderRadius: 2, bgcolor: darkMode ? 'rgba(255,255,255,0.02)' : '#fff' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" fontWeight="bold" noWrap sx={{ fontSize: '0.8rem' }}>
                                                {executor.nome_amigavel || executor.login}
                                            </Typography>
                                            <IconButton size="small" color="error" onClick={() => handleRemoveExecutor(index)} sx={{ p: 0.5 }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                            <Box sx={{ display: 'flex', gap: 0.2 }}>
                                                {['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'].map(dia => (
                                                    <Box key={dia} onClick={() => toggleExecutorDay(index, dia)} sx={{ fontSize: '0.6rem', padding: '2px 4px', borderRadius: '4px', cursor: 'pointer', bgcolor: executor.dias.includes(dia) ? 'primary.main' : 'rgba(128,128,128,0.1)', color: executor.dias.includes(dia) ? 'white' : 'text.secondary', transition: '0.2s' }}>{dia[0]}</Box>
                                                ))}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                            <TextField type="time" label="Início" value={executor.horaInicio} onChange={(e) => handleExecutorTimeChange(index, 'horaInicio', e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} inputProps={{ style: { fontSize: '0.7rem', padding: '4px' } }} />
                                            <TextField type="time" label="Fim" value={executor.horaFim} onChange={(e) => handleExecutorTimeChange(index, 'horaFim', e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} inputProps={{ style: { fontSize: '0.7rem', padding: '4px' } }} />
                                        </Box>
                                    </Card>
                                ))}
                            </Box>
                            <Button 
                                variant="contained" 
                                fullWidth 
                                size="small" 
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} 
                                sx={{ mt: 1, borderRadius: 2, fontWeight: 'bold', py: 1 }}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? "Salvando..." : (id ? "Atualizar" : "Salvar")}
                            </Button>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={8.4} sx={{ height: '100%', position: 'relative' }}>
                        <Paper elevation={3} sx={{ height: '100%', borderRadius: 4, overflow: 'hidden' }}>
                            <MapContainer center={[-18.5122, -44.5550]} zoom={13} style={{ height: '100%', width: '100%' }}>
                                <TileLayer url={darkMode ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png' : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'} />
                                
                                {startPoint && !isNaN(parseFloat(startPoint.latitude)) && (
                                    <Marker position={[parseFloat(startPoint.latitude), parseFloat(startPoint.longitude)]} icon={iconStart}>
                                        <LeafletTooltip permanent direction="top" offset={[0, -20]}>
                                            <Typography variant="caption" fontWeight="bold">PARTIDA</Typography>
                                        </LeafletTooltip>
                                    </Marker>
                                )}

                                {routePoints.map((point, index) => (
                                    <Marker key={point.tempId} position={[parseFloat(point.latitude), parseFloat(point.longitude)]} icon={iconRound}>
                                        <LeafletTooltip permanent direction="top" offset={[0, -20]}>
                                            <Typography variant="caption" fontWeight="bold" sx={{ color: '#000' }}>{index + 1}. {point.nome_fantasia || 'Ponto'}</Typography>
                                        </LeafletTooltip>
                                    </Marker>
                                ))}

                                {endPoint && !isNaN(parseFloat(endPoint.latitude)) && (
                                    <Marker position={[parseFloat(endPoint.latitude), parseFloat(endPoint.longitude)]} icon={iconEnd}>
                                        <LeafletTooltip permanent direction="top" offset={[0, -20]}>
                                            <Typography variant="caption" fontWeight="bold">CHEGADA</Typography>
                                        </LeafletTooltip>
                                    </Marker>
                                )}

                                {polylinePositions.length > 1 && <Polyline positions={polylinePositions} color="#1976d2" weight={4} opacity={0.7} dashArray="10, 10" />}
                                <RecenterMap routePoints={routePoints} startPoint={startPoint} endPoint={endPoint} />
                            </MapContainer>

                            {/* DASHBOARD DE ESTATÍSTICAS */}
                            <Box sx={{ 
                                position: 'absolute', top: 20, right: 20, zIndex: 1000, 
                                display: 'flex', gap: 2 
                            }}>
                                <Paper sx={{ p: 1.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1, bgcolor: darkMode ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)' }}>
                                    <StraightenIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" display="block" color="textSecondary">Distância Total</Typography>
                                        <Typography variant="body2" fontWeight="bold">{routeStats.distance} KM</Typography>
                                    </Box>
                                </Paper>
                                <Paper sx={{ p: 1.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1, bgcolor: darkMode ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)' }}>
                                    <TimerIcon color="secondary" />
                                    <Box>
                                        <Typography variant="caption" display="block" color="textSecondary">Tempo Estimado</Typography>
                                        <Typography variant="body2" fontWeight="bold">{routeStats.time} min</Typography>
                                    </Box>
                                </Paper>
                            </Box>

                            {/* LEGENDA */}
                            <Box sx={{ 
                                position: 'absolute', bottom: 20, left: 20, zIndex: 1000, 
                                bgcolor: darkMode ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)', 
                                p: 2, borderRadius: 3, border: '1px solid rgba(128,128,128,0.2)' 
                            }}>
                                <Typography variant="caption" fontWeight="bold" display="block" mb={1} sx={{ color: darkMode ? '#fff' : '#000' }}>Legenda</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {[
                                         { img: '3011/3011985', label: 'Partida' },
                                         { img: '684/684908', label: 'Ronda' },
                                         { img: '2618/2618245', label: 'Chegada' }
                                     ].map(item => (
                                         <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                             <img src={`https://cdn-icons-png.flaticon.com/512/${item.img}.png`} width="15" alt={item.label} />
                                             <Typography variant="caption" sx={{ color: darkMode ? '#ccc' : '#444' }}>{item.label}</Typography>
                                         </Box>
                                     ))}
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </ThemeProvider>
    );
}
